<template>
  <Suspense>
    <component
      :is="component"
      v-if="component"
      v-bind="panel.props"
      :focused="focused"
      @close="onChildCloseEvent"
    />
    <template #fallback>
      <div class="h-full w-full flex items-center justify-center">
        <o-loader />
      </div>
    </template>
  </Suspense>
</template>

<script setup lang="ts">
import { computed, inject, provide, reactive, watchEffect } from 'vue'
import { OLoader } from '@/components/elements'
import { useAuthStore } from '~/stores/useAuthStore'
import { usePanelStore, type PanelInstance } from '~/stores/usePanelStore'
import { PanelContextKey, type PanelContext } from '~/composables/usePanel'
import { PanelModeKey } from './panelModeKey'

interface Props {
  panel: PanelInstance
  focused: boolean
}

const props = defineProps<Props>()
const store = usePanelStore()
const authStore = useAuthStore()

const component = computed(() => store.getComponentFor(props.panel.key))

// Shared mode ref owned by PanelHost. Falls back to 'panel' if, for any reason,
// this slot renders outside a PanelHost (shouldn't happen in normal flow).
const hostMode = inject(PanelModeKey, null)
const panelModeRef = computed<'panel' | 'modal'>(() => hostMode?.value ?? 'panel')

// One context object that satisfies BOTH the new PanelContext shape (via
// PanelContextKey Symbol) and the legacy PanelLayout shape (via string key 'panel').
// PanelLayout.vue still uses `inject('panel')` with a mutable-field contract
// (writes shouldDismiss etc. directly onto the injected object), so we keep
// those fields as writable slots on the same reactive.
const legacyCtx = reactive({
  get panelMode() { return panelModeRef.value },
  portal: props.panel.portal,
  extension: props.panel.extension,
  close: (force?: boolean) => store.close(props.panel, force),
  mode: () => {
    const next: 'panel' | 'modal' = panelModeRef.value === 'panel' ? 'modal' : 'panel'
    if (hostMode) hostMode.value = next
    authStore.setPanel({ mode: next })
  },
  // legacy writable slots — PanelLayout assigns to these directly
  shouldDismiss: null as (() => boolean) | null,
  willDismiss: null as (() => void) | null,
  didDismiss: null as (() => void) | null,
  didAttemptToDismiss: null as (() => void) | null,
})

// Mirror legacy assignments onto the canonical panel instance so the store's
// close/popstate paths can consult them.
watchEffect(() => {
  props.panel.shouldDismiss = legacyCtx.shouldDismiss
  props.panel.willDismiss = legacyCtx.willDismiss
  props.panel.didDismiss = legacyCtx.didDismiss
  props.panel.didAttemptToDismiss = legacyCtx.didAttemptToDismiss
})

const modernCtx: PanelContext = reactive({
  instance: props.panel,
  portal: props.panel.portal,
  extension: props.panel.extension,
  get panelMode() { return panelModeRef.value },
  close: (force?: boolean) => store.close(props.panel, force),
  replace: (key: string, p?: Record<string, unknown>) => store.replace(key as any, p),
  mode: () => {
    const next: 'panel' | 'modal' = panelModeRef.value === 'panel' ? 'modal' : 'panel'
    if (hostMode) hostMode.value = next
    authStore.setPanel({ mode: next })
  },
  registerShouldDismiss: (fn) => { legacyCtx.shouldDismiss = fn },
  registerWillDismiss: (fn) => { legacyCtx.willDismiss = fn },
  registerDidDismiss: (fn) => { legacyCtx.didDismiss = fn },
  registerDidAttemptToDismiss: (fn) => { legacyCtx.didAttemptToDismiss = fn },
}) as PanelContext

provide(PanelContextKey, modernCtx)
provide('panel', legacyCtx)

function onChildCloseEvent() {
  store.close(props.panel)
}
</script>
